import React from "react";
import { Close } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { IconButton } from "@/components/ui/icon-button";

export interface BadgeWithXProps {
  text?: string;
  icon?: React.ReactElement;
  className?: string;
  textClassName?: string;
  onXClick?: (event: React.MouseEvent) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  readOnly?: boolean;
  showX?: boolean;
  tooltip?: string;
  removeControlFocusable?: boolean;
}

const BadgeWithX: React.FC<BadgeWithXProps> = ({
  text,
  icon,
  className,
  textClassName,
  onClick,
  onXClick,
  readOnly = false,
  showX = false,
  tooltip,
  removeControlFocusable = true
}) => {
  const handleXClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onXClick) {
      onXClick(event);
    }
  };

  const handleBadgeClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[role="button"]')) {
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  const badgeContent = (
    <div
      className={cn(
        "px-[6px] py-1 gap-1 rounded-lg border-[0.5px] border-badge-default flex items-center justify-center bg-badges-default w-fit h-6 group",
        className
      )}
      onClick={handleBadgeClick}
    >
      {icon && icon}
      <span className={cn("text-sm", textClassName)}>{text}</span>
      {!readOnly &&
        onXClick &&
        showX && (
          <IconButton
            {...(removeControlFocusable
              ? { type: "button" as const }
              : { asChild: true })}
            variant="iconNoBg"
            size="xs"
            iconClassName="[&_svg]:size-4"
            onClick={handleXClick}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {removeControlFocusable ? (
              <Close />
            ) : (
              <span
                aria-hidden="true"
                data-test="multiselect-badge-remove-control"
              >
                <Close />
              </span>
            )}
          </IconButton>
        )}
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return badgeContent;
};

export default BadgeWithX;
